import { useState } from "react";
import { usePageTitle } from "./hooks/usePageTitle";

type FAQItem = {
  question: string;
  answer: string;
};

const FAQS: FAQItem[] = [
  {
    question: "What platforms does TerraStation work on?",
    answer:
      "TerraStation runs on both 32-bit and 64-bit versions of Windows 11",
  },
  {
    question: "Is TerraStation available under LINUX or UNIX?",
    answer:
      "No - currently TerraStation is only available for Windows platforms.",
  },
  {
    question: "Does TerraStation run in a network environment?",
    answer:
      "Yes. We can provide a license service for running on Windows networks. This allows you to share one or more licenses over a network.",
  },
  {
    question: "Is TerraStation modular?",
    answer:
      'Yes. Everyone has to have the "Base" TerraStation system, but after that you can purchase or rent only the modules you need.',
  },
  {
    question: "Can I get a trial version of TerraStation?",
    answer:
      'Yes. You can ask us for a Demo version of the software. This comes with a ready to use data set. The Demo version does not allow you to import your own data. <br/><br /> \
    You can request the Demo version by filling in the <a style="color: #3772FF;" href=\'/about-us/contact-us\'>Contact Us</a> form - select "Demo Request" as the subject and tell us where you\'re located.',
  },
  {
    question: "How much does TerraStation cost?",
    answer:
      'TerraStation is a modular system, so the cost will vary depending on which modules you need, whether you are in a network or standalone environment, and how many licenses you require. Use the Contact Us form and select "Sales Query" as the subject. Tell us about your needs and we will prepare a quote for you. \
        <br /> <br /> If you are an existing TerraStation client looking for extra licenses or additional modules, please email your normal sales contact instead.',
  },
  {
    question: "Do I have to purchase TerraStation?",
    answer:
      "No. We also offer a rental scheme. There is a 3 month minimum rental requirement for new clients, after which you can rent by the month. You can switch off the software if you don't think you will need it for a while. When you are ready to use it again, contact us and we will issue you a new license file. \
    <br /> <br /> We do not provide lease/purchase options for our products. Your options are to buy it, or rent it.",
  },
  {
    question: "How do you provide support for your software?",
    answer:
      "TERRASCIENCES provides support for our Denver office during normal business hours.\
    <br /> <br />Support is available to clients with existing support contracts or current rental agreements. \
    <br /> <br />Support is primarily provided by email. We can also talk with you by phone, Skype or Zoom if required. \
    <br /> <br />In some cases we can visit client sites, although we do charge per-visit for this.",
  },
  {
    question: "Do you provide training?",
    answer:
      "<i>Note that at present we are unable to travel to client sites to provide training due to Covid-19 restrictions</i>.",
  },
  {
    question: "Do you have an Academic discount program?",
    answer:
      "Yes. Academic licenses are available for colleges and universities providing the software is used only for teaching and/or research purposes. No revenue generating activities may be performed with academic licenses. There is a nominal fee for initial purchase and ongoing maintenance. \
        <br /> <br />Use the <a style='color: #3772FF;' href='/about-us/contact-us'>Contact Us</a> form with \"Sales Query\" as the subject for more information.",
  },
  {
    question: "What is the hardware specification needed to run TerraStation?",
    answer:
      'TerraStation runs on any standard PC - desktop or laptop. \
    <br />You need to have one of the following operating systems: \
    <ul style="list-style-type:circle; padding-left: 16px;"> \
        <li>Windows 7</li>\
        <li>Windows 8 or 8.1</li>\
        <li>Windows 10 or 11</li>\
    </ul> \
    <br /> There are no specific hardware requirements, but a fast disk controller is advantageous, and if you are planning on a lot of borehole imaging work, two monitors and a large disk capacity are recommended, but not critical. \
    <br /> <br />TerraStation will not run on lower-level Microsoft Surface devices (ones which do not have an Intel x86 compatible processor), but it does run on the higher-level Surface Pro models. \
    ',
  },
  {
    question: "What is the licensing mechanism for AutoDidge?",
    answer:
      "AutoDidge is only available as single user licenses. It is licensed via a small security file that is issued by TERRASCIENCES when your purchase is made and verified. It ties the license to a specific computer. \
    <br /> <br />We do not rent AutoDidge, it is for purchase only. \
    <br /> <br />There is no dongle or network licensing available for AutoDidge.",
  },
  {
    question:"Is TerraStation compatible with 64-bit versions of Windows?",
    answer: "It is.\
    <br /> <br />TerraStation is currently a 64-bit executable, although we can also still provide a 32-bit version if necessary. \
    <br /> <br />AutoDidge and our other utility programs are currently 32-bit, but they work fine on 64-bit versions of Windows. \ "
  },
  {
    question: "What modules are included in the TerraStation bundles?",
    answer: "TerraStation is a modular system, which allows a wide variety of configurations. Deciding which modules you need is often tricky, so we have created some bundles of modules that suit different types of clients. These are better value than buying or renting the constituent modules separately.\
    <br /> <br />You can download our Feature Matrix, which shows which functions are included in each bundle, from <a style=\"color: #3772FF;\" href=\"\">here</a>."
  }
];

function SalesFAQ() {
  usePageTitle('Sales FAQ')
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  return (
    <div
      id="ts-salesfaq-wrapper"
      className="w-full h-full flex items-center justify-center"
    >
      <div
        id="ts-salesfaq-content"
        className="w-2/3 p-4 h-full flex flex-col items-center"
      >
        <div className="w-full relative flex items-center justify-center text-2xl p-2 border-b border-light-border">
          Sales FAQ
        </div>

        <div className="w-full mt-4 text-left text-light-text">
          <div className="space-y-3">
            {FAQS.map((item, i) => {
              const isOpen = openIndex === i;
              const btnId = `faq-btn-${i}`;
              const panelId = `faq-panel-${i}`;

              return (
                <div key={i} className="border border-light-border rounded">
                  <h3>
                    <button
                      id={btnId}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggle(i)}
                      className="w-full text-left p-3 flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary bg-white hover:bg-gray-50 cursor-pointer"
                    >
                      <span className="font-bold text-lg text-light-text-link">
                        {item.question}
                      </span>
                      <span className="ml-2 text-sm text-light-text">
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>
                  </h3>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={btnId}
                    className={`px-3 pb-3 transition-max-h duration-200 overflow-hidden ${
                      isOpen ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <div
                      className="pt-2 text-normal text-light-text"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesFAQ;
